import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { deepLinkToPath, getPlatform } from '@/lib/platform';
import { resumeSyncOnForeground } from '@/hooks/useMatchSyncFlush';
import { handleNativeBack } from './nativeBackButton';

/**
 * Native app only (no-op on the website): opens the route of the link the app
 * was opened with, sends the entry queue again when the app returns to the
 * foreground, and handles the Android Back button.
 */
export function useNativeAppEvents(): void {
  const navigate = useNavigate();
  const location = useLocation();
  // The router hands out a new `navigate` on each location change; listeners stay put.
  const navigateRef = useRef(navigate);
  const currentPathRef = useRef('');
  const pathnameRef = useRef(location.pathname);
  useLayoutEffect(() => {
    navigateRef.current = navigate;
    pathnameRef.current = location.pathname;
    currentPathRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [navigate, location]);

  useEffect(() => {
    const platform = getPlatform();
    const offLinks = platform.deepLinks.subscribe((url) => {
      const path = deepLinkToPath(url);
      if (!path) return;
      // At cold start the link arrives twice (appUrlOpen and getLaunchUrl): one history entry, so
      // Back does not land on the same page.
      navigateRef.current(path, { replace: path === currentPathRef.current });
      // Both deliveries may land before the router renders the first one.
      currentPathRef.current = path;
    });
    const offResume = platform.lifecycle.onResume(resumeSyncOnForeground);
    const offBack = platform.lifecycle.onBackButton(() =>
      handleNativeBack({
        pathname: pathnameRef.current,
        navigate: navigateRef.current,
        exit: platform.lifecycle.exit,
      }),
    );
    return () => {
      offLinks();
      offResume();
      offBack();
    };
  }, []);
}
